/** A book's record page. Books can belong to several systems, shown as equal links. */
import { Link, useParams } from 'react-router'
import { BookShelf } from '../components/Cards'
import { Chips, ErrorState, Loading } from '../components/Bits'
import {
  Attributions,
  ExternalIds,
  Facts,
  GapList,
  LinkList,
  MergedRedirect,
  Prose,
  RecordHeader,
  RelationList,
  type Crumb,
} from '../components/Record'
import { categoryLabel, languageName, num, plural, pubDate, ROLE_LABELS } from '../lib/format'
import { AVAILABILITY_LABELS, BOOK_RELATION_INVERSE, BOOK_RELATION_LABELS, FORMAT_LABELS, label } from '../lib/labels'
import { useQuery } from '../lib/query'
import { useTitle } from '../lib/useTitle'
import type { BookCard, BookDetail, Page } from '../lib/types'
import { coverWithheld, CREDIT_ROLES } from '../../shared/model'

export function levels(min: number | null, max: number | null): string {
  if (min !== null && max !== null) return min === max ? `Level ${min}` : `Levels ${min}–${max}`
  if (min !== null) return `Level ${min}+`
  if (max !== null) return `Up to level ${max}`
  return ''
}

export function BookPage() {
  const { id = '' } = useParams()
  const { data: b, error, refresh } = useQuery<BookDetail>(`/api/v1/books/${id}`)
  const firstSystem = b?.doc.systems[0]?.id
  const { data: more } = useQuery<Page<BookCard>>(firstSystem ? `/api/v1/systems/${firstSystem}/books?limit=7` : null)
  useTitle(b?.doc.title)

  if (error) return <ErrorState error={error} retry={refresh} />
  if (!b) return <Loading label="Loading book" />
  if (b.merged_into) return <MergedRedirect type="book" to={b.merged_into} />
  const d = b.doc
  const crumbs: Crumb[] = d.systems.length
    ? d.systems.map((s) => ({ label: s.name, to: `/systems/${s.id}` }))
    : [{ label: 'Books', to: '/books' }]
  const byRole = CREDIT_ROLES.map((role) => ({ role, people: d.credits.filter((c) => c.role === role) })).filter(
    (r) => r.people.length,
  )
  const date = pubDate(d.year, d.month, d.day)
  const meta = [
    categoryLabel(d.category),
    date,
    d.page_count ? plural(d.page_count, 'page') : '',
    levels(d.level_min, d.level_max),
  ]

  return (
    <article className="page record">
      <RecordHeader
        type="book"
        id={b.id}
        crumbs={crumbs}
        title={d.title}
        subtitle={
          <>
            {d.subtitle && <span className="record-subtitle-text">{d.subtitle}</span>}
            <span>{meta.filter(Boolean).join(' · ')}</span>
          </>
        }
        cover={d.cover}
        coverWithheld={coverWithheld(d)}
        score={b.completeness.score}
        editHref={`/books/${b.id}/edit`}
        historyHref={`/books/${b.id}/history`}
        pending={b.pending_edits}
        pendingHref={`/review?entity=${b.id}`}
        deleted={b.deleted}
      >
        {byRole.some((r) => r.role === 'author') && (
          <p className="record-byline">
            {byRole
              .filter((r) => r.role === 'author')
              .flatMap((r) => r.people)
              .map((c, i) => (
                <span key={c.creator.id ?? c.creator.name}>
                  {i > 0 && ', '}
                  {c.creator.id ? <Link to={`/creators/${c.creator.id}`}>{c.creator.name}</Link> : c.creator.name}
                </span>
              ))}
          </p>
        )}
        {d.series.length > 0 && (
          <p className="record-series">
            {d.series.map((s, i) => (
              <span key={s.id ?? s.name}>
                {i > 0 && ' · '}
                {s.id ? <Link to={`/series/${s.id}`}>{s.name}</Link> : s.name}
                {s.number && ` #${s.number}`}
              </span>
            ))}
          </p>
        )}
        {b.variant_parent && (
          <p className="notice notice-variant">
            A {d.variant_kind ? d.variant_kind.replace(/-/g, ' ') : 'variant'} of{' '}
            <Link to={`/books/${b.variant_parent.id}`}>{b.variant_parent.title}</Link>
            {d.variant_label && ` (${d.variant_label})`}
          </p>
        )}
      </RecordHeader>

      <div className="record-body">
        <div className="record-main">
          <Prose text={d.description} />
          <Attributions attributions={d.attributions} />
          <GapList missing={b.completeness.missing} score={b.completeness.score} editHref={`/books/${b.id}/edit`} />
          <RelationList
            type="book"
            outgoing={d.relations.map((r) => ({ type: r.type, id: r.book.id, name: r.book.name }))}
            incoming={b.incoming}
            labels={BOOK_RELATION_LABELS}
            inverse={BOOK_RELATION_INVERSE}
          />
          {b.variants.length > 0 && (
            <section className="section">
              <h2 className="section-title">Other versions</h2>
              <ul className="variant-list">
                {b.variants.map((v) => (
                  <li key={v.id}>
                    <Link to={`/books/${v.id}`}>{v.title}</Link>
                    <span>{[v.variant_kind?.replace(/-/g, ' '), v.variant_label].filter(Boolean).join(' · ')}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
        <aside className="record-side">
          <Facts
            facts={[
              {
                label: d.systems.length > 1 ? 'Systems' : 'System',
                value: d.systems.length > 0 && (
                  <ul className="inline-list">
                    {d.systems.map((s) => (
                      <li key={s.id}>
                        <Link to={`/systems/${s.id}`}>{s.name}</Link>
                      </li>
                    ))}
                  </ul>
                ),
              },
              ...byRole.map((r) => ({
                label: r.people.length > 1 ? `${ROLE_LABELS[r.role]}s` : ROLE_LABELS[r.role],
                value: (
                  <ul className="inline-list">
                    {r.people.map((c) => (
                      <li key={c.creator.id ?? c.creator.name}>
                        {c.creator.id ? <Link to={`/creators/${c.creator.id}`}>{c.creator.name}</Link> : c.creator.name}
                      </li>
                    ))}
                  </ul>
                ),
              })),
              {
                label: d.publishers.length > 1 ? 'Publishers' : 'Publisher',
                value: d.publishers.length > 0 && (
                  <ul className="inline-list">
                    {d.publishers.map((p) => (
                      <li key={p.id ?? p.name}>{p.id ? <Link to={`/publishers/${p.id}`}>{p.name}</Link> : p.name}</li>
                    ))}
                  </ul>
                ),
              },
              {
                label: 'Category',
                value: <Link to={`/books?category=${d.category}`}>{categoryLabel(d.category)}</Link>,
              },
              { label: 'Published', value: date },
              { label: 'Pages', value: d.page_count ? num(d.page_count) : '' },
              { label: 'For levels', value: levels(d.level_min, d.level_max) },
              { label: 'Formats', value: d.formats.map((f) => label(FORMAT_LABELS, f)).join(', ') },
              {
                label: 'Availability',
                value: d.availability && (
                  <Link to={`/books?availability=${d.availability}`}>{label(AVAILABILITY_LABELS, d.availability)}</Link>
                ),
              },
              { label: 'Language', value: languageName(d.language) },
              { label: 'Version', value: d.version },
              {
                label: 'ISBN',
                value: d.isbn && (
                  <span className="mono" translate="no">
                    {d.isbn}
                  </span>
                ),
              },
              {
                label: 'Product code',
                value: d.product_code && (
                  <span className="mono" translate="no">
                    {d.product_code}
                  </span>
                ),
              },
              { label: 'Elsewhere', value: d.external_ids.length > 0 && <ExternalIds ids={d.external_ids} /> },
              { label: 'License', value: [d.license, d.license_notes].filter(Boolean).join('. ') },
              {
                label: 'Genres',
                value: d.genres.length > 0 && (
                  <Chips items={d.genres} hrefFor={(g) => `/books?genre=${encodeURIComponent(g)}`} />
                ),
              },
              {
                label: 'Tags',
                value: d.tags.length > 0 && (
                  <Chips items={d.tags} hrefFor={(t) => `/books?tag=${encodeURIComponent(t)}`} />
                ),
              },
              { label: 'File fingerprints', value: d.fingerprints.length ? `${d.fingerprints.length} known` : '' },
              { label: 'Content', value: d.is_explicit ? 'Explicit' : '' },
            ]}
          />
          <LinkList title="Links" links={d.urls} />
        </aside>
      </div>

      {more && more.items.filter((x) => x.id !== b.id).length > 0 && (
        <section className="section">
          <div className="section-head">
            <h2 className="section-title">More for {d.systems[0]!.name}</h2>
            <Link to={`/systems/${d.systems[0]!.id}`} className="section-more">
              See the shelf
            </Link>
          </div>
          <BookShelf
            items={more.items.filter((x) => x.id !== b.id).slice(0, 6)}
            label={`More books for ${d.systems[0]!.name}`}
            showSystems={false}
          />
        </section>
      )}
    </article>
  )
}
